import { useState } from 'react';
import ExpandLessRoundedIcon from '@material-ui/icons/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@material-ui/icons/ExpandMoreRounded';

import { PdvDialog } from 'componentsV2/pdv';
import type { PdvFlag } from 'Services/Query/Pdv';
import {
  PDV_FLAG_STATUS,
  PDV_ISSUE_LABEL,
  pdvDate,
  pdvDateTime,
  pdvFlagDisplay,
  pdvReasonLabel,
  pdvRepHistory
} from 'utilsV2/pdv';

import { useStyles } from './styles';

/** One report as the rep sees it: status, what was said, what happened to it. */
export const FeedbackCard = ({ flag, me }: { flag: PdvFlag; me: string }) => {
  const classes = useStyles();
  const [open, setOpen] = useState(true);
  const s = PDV_FLAG_STATUS[pdvFlagDisplay(flag)];
  const history = pdvRepHistory(flag, me);

  return (
    <article className={classes.fbCard}>
      <div className={classes.fbHead}>
        <span
          className={classes.status}
          style={{
            background: s.bg,
            color: s.fg,
            boxShadow: s.border ? `inset 0 0 0 1px ${s.border}` : undefined
          }}>
          {s.label}
        </span>
        <strong>
          {PDV_ISSUE_LABEL[flag.issue ?? 'other']}
          {flag.attr ? ` · ${flag.attr}` : ''}
        </strong>
        <span className={classes.date}>
          {flag.createdBy === me
            ? 'Reported by you'
            : `Reported by ${flag.createdBy}`}{' '}
          · {pdvDate(flag.createdAt)}
        </span>
      </div>
      <dl className={classes.fields}>
        {flag.issue === 'wrong' && flag.reportedValue && (
          <div>
            <dt>Value you saw</dt>
            <dd>{flag.reportedValue}</dd>
          </div>
        )}
        {flag.note && (
          <div>
            <dt>Comment</dt>
            <dd>“{flag.note}”</dd>
          </div>
        )}
        {flag.outcome === 'not_issue' && flag.reason && (
          <div>
            <dt>Closed because</dt>
            <dd>{pdvReasonLabel(flag.reason)}</dd>
          </div>
        )}
        {flag.resolutionNote && (
          <div>
            <dt>Note from product team</dt>
            <dd>“{flag.resolutionNote}”</dd>
          </div>
        )}
      </dl>
      {history.length > 0 && (
        <>
          <button
            type="button"
            className={classes.historyHead}
            aria-expanded={open}
            onClick={() => setOpen(o => !o)}>
            History
            {open ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
          </button>
          {open && (
            <div className={classes.history}>
              {history.map((h, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <div key={`${h.at}-${i}`}>
                  <b>{h.line}</b> · {pdvDateTime(h.at)}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </article>
  );
};

interface Props {
  sku: string;
  desc?: string;
  flags: PdvFlag[];
  me: string;
  onClose: () => void;
}

/** Every report on this product, closed ones included, with what happened to it. */
export const FeedbackList = ({ sku, desc, flags, me, onClose }: Props) => {
  const classes = useStyles();
  return (
    <PdvDialog
      surface="grey"
      size="md"
      onClose={onClose}
      title={`Feedback (${flags.length})`}
      subtitle={`${desc ? `${desc} · ` : ''}SKU ${sku}`}>
      <div className={classes.list}>
        {flags.map(f => (
          <FeedbackCard
            key={f._id ?? `${f.createdAt}-${f.attr}`}
            flag={f}
            me={me}
          />
        ))}
      </div>
    </PdvDialog>
  );
};
